import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { EditIcon, NotesIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";
import { format, FORMATS, parseJSON } from "utils/datetime";

import Box from "components/Box";
import Button from "components/Button";
import IconButton from "components/IconButton";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import RichTextEditorField from "components/RichTextEditorField";
import SanitizeHtml from "components/SanitizeHtml";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";

interface IRemarkData {
  title: string;
  remark: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string | null;
  onUpdate: (value: string) => void;
  remarkType: "public" | "private";
  isRemarkLoading: boolean;
  dataTestid?: string;
}

const RemarkData = ({
  title,
  remark,
  updatedAt,
  createdBy,
  updatedBy,
  onUpdate,
  remarkType,
  dataTestid,
  isRemarkLoading,
}: IRemarkData) => {
  const [value, setValue] = useCaching(
    EMPTY_VALUE_REGEX.test(remark) ? "" : remark
  );
  const [isEdit, setIsEdit] = useState(false);
  const handleChange = useCallback(
    ({ newValue }: { newValue: string }) => {
      setValue(newValue);
    },
    [setValue]
  );

  const handleCancel = useCallback(() => {
    setValue(EMPTY_VALUE_REGEX.test(remark) ? "" : remark);
    setIsEdit(false);
  }, [setValue, setIsEdit, remark]);

  const handleSaveChanges = useCallback(() => {
    setIsEdit(false);
    onUpdate(value);
  }, [onUpdate, value]);

  const isRemarkCreated = useMemo(
    () => !_.isEmpty(updatedAt) && _.isEmpty(updatedBy) && !_.isEmpty(remark),
    [updatedAt, updatedBy, remark]
  );

  const isRemarkUpdated = useMemo(
    () => !_.isEmpty(updatedAt) && !_.isEmpty(updatedBy) && !_.isEmpty(remark),
    [updatedAt, updatedBy, remark]
  );

  const separatorColor = useMemo(
    () => (remarkType === "public" ? colors.blue100 : colors.pink100),
    [remarkType]
  );
  const iconBackgroundColor = useMemo(
    () => (remarkType === "public" ? colors.blue10 : colors.pink10),
    [remarkType]
  );

  return (
    <>
      <Stack direction="column" spacing={1}>
        <Stack alignItems="center" direction="row" spacing={1}>
          <Stack
            sx={{
              backgroundColor: iconBackgroundColor,
            }}
            borderRadius="10px"
            alignItems="center"
            justifyContent="center"
            height="20px"
            width="20px"
          >
            <NotesIcon
              style={{ width: 16, height: "auto", color: separatorColor }}
            />
          </Stack>
          <Text variant="label" color={separatorColor}>
            {title}
          </Text>
        </Stack>
      </Stack>
      {!isEdit && _.isEmpty(value) && (
        <Stack
          direction="row"
          justifyContent="space-between"
          data-testid={dataTestid}
        >
          <Text
            variant="text2"
            sx={{
              paddingLeft: theme.spacing(1),
              color: colors.gray60,
            }}
            data-testid={`${dataTestid}-value`}
          >
            No remarks added… Add some lines by clicking the edit sign.
          </Text>
          <IconButton
            size="small"
            title="Edit"
            onClick={() => setIsEdit(true)}
            dataTestid={`${dataTestid}-edit-button`}
          >
            <EditIcon />
          </IconButton>
        </Stack>
      )}
      {(isEdit || value) && (
        <Box
          sx={{
            paddingLeft: theme.spacing(1),
          }}
          data-testid={dataTestid}
        >
          {!isEdit && !_.isEmpty(value) && (
            <Stack
              direction="row"
              justifyContent="space-between"
              data-testid={dataTestid}
            >
              <Box
                sx={{
                  marginBottom: theme.spacing(1),
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                }}
                data-testid={`${dataTestid}-value`}
              >
                <SanitizeHtml html={value} truncateLines={10} />
              </Box>
              <IconButton
                size="small"
                title="Edit"
                onClick={() => setIsEdit(true)}
                dataTestid={`${dataTestid}-edit-button`}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          )}
          {isEdit && (
            <Stack spacing={1} data-testid={`${dataTestid}-edit`}>
              <RichTextEditorField
                defaultValue={value}
                onChange={(value) => {
                  handleChange({ newValue: value });
                }}
              />
              <Stack direction="row" justifyContent="end" spacing={2}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleCancel}
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  onClick={handleSaveChanges}
                  data-testid="submit-button"
                >
                  Save changes
                </Button>
              </Stack>
            </Stack>
          )}
          {isRemarkLoading ? (
            <Skeleton variant="text" width={250} height={16} />
          ) : (
            <>
              {(isRemarkCreated || isRemarkUpdated) && (
                <Text
                  variant="text4"
                  sx={{ color: colors.gray60 }}
                  data-testid={`${dataTestid}-last-edited`}
                >
                  {isRemarkCreated && (
                    <>
                      Created on{" "}
                      {format(parseJSON(updatedAt), FORMATS.FRONTEND.DATE)} by{" "}
                      {createdBy}
                    </>
                  )}
                  {isRemarkUpdated && (
                    <>
                      Last edited on{" "}
                      {format(parseJSON(updatedAt), FORMATS.FRONTEND.DATE)} by{" "}
                      {updatedBy}
                    </>
                  )}
                </Text>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default RemarkData;
